import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  // Mobile accordion state — collapsed by default per team feedback
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);
  const aboutCloseTimer = useRef(null);
  const servicesCloseTimer = useRef(null);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setAboutOpen(false);
    setMobileAboutOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setServicesOpen(false);
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  // Helpers: extended close delay (400ms) so user has plenty of time to reach submenu
  const openAbout = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    setServicesOpen(false);
    setAboutOpen(true);
  };
  const scheduleAboutClose = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    aboutCloseTimer.current = setTimeout(() => setAboutOpen(false), 400);
  };
  const openServices = () => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    setAboutOpen(false);
    setServicesOpen(true);
  };
  const scheduleServicesClose = () => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    servicesCloseTimer.current = setTimeout(() => setServicesOpen(false), 400);
  };

  const servicesActive = ['/services', '/education', '/courses', '/signup', '/consultation', '/resources'].includes(location.pathname);
  const aboutActive = ['/about', '/board-members', '/insights', '/resources'].includes(location.pathname) || location.pathname.startsWith('/insights/') || location.pathname.startsWith('/resources/');

  const navLink = (active) =>
    `rounded-xl px-4 py-2 text-sm font-medium transition-colors normal-case ${
      active ? 'text-primary-500' : 'text-gray-800 hover:text-primary-500'
    }`;

  const closeAll = () => {
    setAboutOpen(false);
    setServicesOpen(false);
    setMobileAboutOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <motion.header
      className="fixed top-0 z-50 w-full border-b border-primary-200 bg-white shadow-sm"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.45 }}
    >
      <nav ref={navRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3" onClick={closeAll}>
            <img 
              src="/assets/images/acdc-logo.png"
              onError={(e) => { e.currentTarget.src = '/assets/images/acdc-logo-backup.png'; }}
              alt="ACDC logo"
              className="h-16 w-auto object-contain md:h-328"
            />
            {/*<div className="hidden leading-tight sm:block">
              <div className="text-lg font-semibold uppercase tracking-[0.08em] text-primary-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                Asian Community
              </div>
              <div className="text-xs uppercase tracking-[0.12em] text-gray-500">
                Development Center
              </div>
            </div>*/}
          </Link>

          <div className="hidden items-center gap-2 md:flex">
            {/* About dropdown */}
            <div className="relative" onMouseEnter={openAbout} onMouseLeave={scheduleAboutClose}>
              <button
                onClick={() => (aboutOpen ? setAboutOpen(false) : openAbout())}
                className={`${navLink(aboutActive)} flex items-center gap-1`}
                type="button"
                aria-expanded={aboutOpen}
                aria-haspopup="menu"
              >
                About
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <AnimatePresence>
                {aboutOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute left-0 top-full mt-2 w-72 rounded-2xl border border-gray-800 bg-gray-900 p-2 shadow-2xl"
                    role="menu"
                    onMouseEnter={openAbout}
                    onMouseLeave={scheduleAboutClose}
                  >
                    {[
                      ['Our Mission', '/about'],
                      ['Board Members', '/board-members'],
                      //['Our Team', '/team'],
                      ['Resources', '/resources']
                    ].map(([label, to]) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={closeAll}
                        className="block rounded-xl px-4 py-3 text-sm text-gray-300 transition-colors hover:bg-gray-800 hover:text-white normal-case"
                        role="menuitem"
                      >
                        {label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/events" className={navLink(location.pathname === '/events')}>Events</Link>

            <div className="relative" onMouseEnter={openServices} onMouseLeave={scheduleServicesClose}>
              <button
                onClick={() => (servicesOpen ? setServicesOpen(false) : openServices())}
                className={`${navLink(servicesActive)} flex items-center gap-1`}
                type="button"
                aria-expanded={servicesOpen}
                aria-haspopup="menu"
              >
                Services
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute left-0 top-full mt-2 w-72 rounded-2xl border border-gray-800 bg-gray-900 p-2 shadow-2xl"
                    role="menu"
                    onMouseEnter={openServices}
                    onMouseLeave={scheduleServicesClose}
                  >
                    {[
                      ['Consulting Services', '/services'],
                      ['Education Services', '/education']
                    ].map(([label, to]) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={closeAll}
                        className="block rounded-xl px-4 py-3 text-sm text-gray-300 transition-colors hover:bg-gray-800 hover:text-white normal-case"
                        role="menuitem"
                      >
                        {label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/volunteer-opportunities" className={navLink(location.pathname === '/volunteer-opportunities')}>Volunteer Opportunities</Link>
            <Link to="/donate" className="btn-header-donate text-sm">Donate</Link>
            <Link to="/contact" className="btn-secondary px-5 py-2.5 text-sm">Contact Us</Link>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsOpen((value) => !value)}
              className="rounded-md p-2 text-gray-800 transition-colors hover:text-primary-700"
              type="button"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu — ACCORDION style: About + Services collapse/expand on tap (per team feedback) */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-100 bg-white md:hidden"
            >
              <div className="max-h-[70vh] overflow-y-auto space-y-1 px-4 py-4">
                {/* About — collapsible */}
                <div className="overflow-hidden rounded-xl bg-primary-50">
                  <button
                    type="button"
                    onClick={() => setMobileAboutOpen((v) => !v)}
                    aria-expanded={mobileAboutOpen}
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold uppercase tracking-[0.12em] text-primary-500"
                  >
                    About
                    <svg className={`h-4 w-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileAboutOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <Link to="/about" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Our Mission</Link>
                        <Link to="/board-members" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Board Members</Link>
                        {/* <Link to="/team" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Our Team</Link> */}
                        <Link to="/resources" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Resources</Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link to="/events" className="block rounded-xl px-4 py-3 text-gray-800 transition-colors hover:bg-primary-50 hover:text-primary-700 normal-case">Events</Link>

                {/* Services — collapsible */}
                <div className="overflow-hidden rounded-xl bg-primary-50">
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    aria-expanded={mobileServicesOpen}
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold uppercase tracking-[0.12em] text-primary-500"
                  >
                    Services
                    <svg className={`h-4 w-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileServicesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <Link to="/services" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Business Services</Link>
                        <Link to="/education" className="block px-6 py-3 text-gray-800 transition-colors hover:bg-white hover:text-primary-700 normal-case">Education Services</Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link to="/volunteer-opportunities" className="block rounded-xl px-4 py-3 text-gray-800 transition-colors hover:bg-primary-50 hover:text-primary-700 normal-case">Volunteer Opportunities</Link>
                <Link to="/donate" className="btn-primary block px-4 py-3 text-center font-medium normal-case">Donate</Link>
                <Link to="/contact" className="block rounded-xl px-4 py-3 text-gray-800 transition-colors hover:bg-primary-50 hover:text-primary-700 normal-case">Contact Us</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;
